import type { ReactNode } from 'react';

import { DropdownContext } from './context';
import { useDropdownState } from './use-dropdown-state';

/**
 * Props for the Dropdown component
 */
export interface DropdownProps {
  /** Dropdown content (DropdownTrigger, DropdownMenu, etc.) */
  children: ReactNode;
  /** Callback when an item is selected */
  onSelect?: (id: string) => void;
  /** Optional className for the container */
  className?: string;
  /** Size variant - affects item padding */
  size?: 'sm' | 'md';
  /** Controlled open state */
  open?: boolean;
  /** Callback when open state changes */
  onOpenChange?: (open: boolean) => void;
  /** Minimum width in pixels */
  minWidth?: number;
  /** Maximum width in pixels */
  maxWidth?: number;
  /** Whether to close dropdown when an item is selected (default: true) */
  closeOnSelect?: boolean;
}

/**
 * Dropdown root component - provides context for all dropdown children
 *
 * This is an action menu component using compound components pattern.
 * For selection (single/multi), use Select component.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <Dropdown onSelect={handleAction}>
 *   <DropdownTrigger>Actions</DropdownTrigger>
 *   <DropdownMenu>
 *     <DropdownMenuItem id="edit">Edit</DropdownMenuItem>
 *     <DropdownMenuItem id="duplicate">Duplicate</DropdownMenuItem>
 *     <DropdownMenuSeparator />
 *     <DropdownMenuItem id="delete" destructive>Delete</DropdownMenuItem>
 *   </DropdownMenu>
 * </Dropdown>
 *
 * // With custom trigger content
 * <Dropdown onSelect={handleAction}>
 *   <DropdownTrigger>
 *     {({ isOpen }) => (
 *       <>
 *         <MenuIcon />
 *         <span>Menu</span>
 *       </>
 *     )}
 *   </DropdownTrigger>
 *   <DropdownMenu>
 *     <DropdownMenuItem id="settings">Settings</DropdownMenuItem>
 *     <DropdownMenuItem id="logout">Logout</DropdownMenuItem>
 *   </DropdownMenu>
 * </Dropdown>
 *
 * // With header and footer
 * <Dropdown onSelect={handleAction}>
 *   <DropdownTrigger>Options</DropdownTrigger>
 *   <DropdownMenu
 *     header={<div>Choose an action</div>}
 *     footer={<Button size="sm">Cancel</Button>}
 *   >
 *     <DropdownMenuItem id="option1">Option 1</DropdownMenuItem>
 *     <DropdownMenuItem id="option2">Option 2</DropdownMenuItem>
 *   </DropdownMenu>
 * </Dropdown>
 * ```
 */
export function Dropdown({
  children,
  onSelect,
  className = '',
  size = 'md',
  open,
  onOpenChange,
  minWidth,
  maxWidth,
  closeOnSelect = true,
}: DropdownProps) {
  const contextValue = useDropdownState({
    onSelect,
    size,
    open,
    onOpenChange,
    minWidth,
    maxWidth,
    closeOnSelect,
  });

  return (
    <DropdownContext.Provider value={contextValue}>
      <div className={`relative inline-block ${className}`}>{children}</div>
    </DropdownContext.Provider>
  );
}
